import { Box, StackProps } from "@mui/material";
import Button from "@mui/material/Button";
import CardActions from "@mui/material/CardActions";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useFormik } from "formik";
import _ from "lodash";
import { MuiColorInput } from "mui-color-input";

import { Option } from "~ui-components/components/atoms/option";
import { VISUALISATION_COLORS } from "~ui-components/constants/colors";
import { PolygonCategoryConfigs } from "~ui-components/types/__generated/gql/graphql";

type Props = StackProps & {
	form: ReturnType<typeof useFormik<PolygonCategoryConfigs>>;
	attributes: string[];
	onOpenCustomLegend: () => void;
};

export const PolygonCategoryStyle = (props: Props) => {
	const { onOpenCustomLegend, attributes, form } = props;
	const { values, handleChange, handleBlur, handleSubmit, setFieldValue } =
		form;

	return (
		<>
			<Stack spacing={3}>
				<FormControl sx={{ width: 200 }}>
					<InputLabel>Colour by</InputLabel>
					<Select
						name="valueField"
						onChange={(e) => {
							handleChange(e);
							void setFieldValue("isSymbolValuesCustomised", false);
						}}
						value={values.valueField}>
						{attributes?.map((value) => (
							<Option
								key={value}
								value={value}>
								{value}
							</Option>
						))}
					</Select>
				</FormControl>

				<FormControl sx={{ width: 200 }}>
					<InputLabel>Color Ramp</InputLabel>
					<Select
						name="colorRamp"
						value={values?.colorRamp}
						onChange={(e) => {
							handleChange(e);
							void setFieldValue("isSymbolValuesCustomised", false);
						}}
						renderValue={(value) =>
							value && VISUALISATION_COLORS[value] ? (
								<Stack direction="row">
									{VISUALISATION_COLORS[value][9].slice(2).map((color) => (
										<Box
											key={color}
											sx={{ width: 20, height: 20, backgroundColor: color }}
										/>
									))}
								</Stack>
							) : null
						}>
						{Object.keys(VISUALISATION_COLORS).map((key) => (
							<Option
								key={key}
								value={key}>
								<Stack direction="row">
									{VISUALISATION_COLORS[key][9].slice(2).map((color) => (
										<Box
											key={color}
											sx={{ width: 20, height: 20, backgroundColor: color }}
										/>
									))}
								</Stack>
							</Option>
						))}
					</Select>
				</FormControl>

				<Typography variant="subtitle1">Fill and Stroke</Typography>

				<FormControl sx={{ width: 200 }}>
					<InputLabel>Fill style</InputLabel>
					<Select
						name="symbolStyle.fill.fillType"
						onChange={handleChange}
						value={values?.symbolStyle?.fill?.fillType}>
						<Option value="solid">Solid</Option>
						<Option value="verticalline">Vertica Lline</Option>
						<Option value="horizontalline">Horizontal Line</Option>
						<Option value="slash">Slash</Option>
						<Option value="backslash">Back Slash</Option>
						<Option value="grid">Grid</Option>
						<Option value="diagonalgrid">Diagonal Grid</Option>
						<Option value="none">None</Option>
					</Select>
				</FormControl>
				<FormControl sx={{ width: 200 }}>
					<InputLabel>Fill Opacity</InputLabel>
					<Select
						name="symbolStyle.fill.opacity"
						onChange={handleChange}
						value={values.symbolStyle?.fill?.opacity}>
						{_.range(0, 11).map((value) => (
							<Option
								key={value}
								value={value / 10}>
								{value * 10}%
							</Option>
						))}
					</Select>
				</FormControl>
				<FormControl sx={{ width: 200 }}>
					<InputLabel>Stroke colour</InputLabel>
					<MuiColorInput
						sx={{ width: 200 }}
						format="hex"
						value={values.symbolStyle?.stroke?.color ?? "#fff"}
						onChange={(value) =>
							form.setFieldValue("symbolStyle.stroke.color", value)
						}
					/>
				</FormControl>
				<FormControl sx={{ width: 200 }}>
					<InputLabel>Stroke opacity</InputLabel>
					<Select
						name="symbolStyle.stroke.opacity"
						onChange={handleChange}
						value={values.symbolStyle?.stroke?.opacity}>
						{_.range(0, 11).map((value) => (
							<Option
								key={value}
								value={value / 10}>
								{value * 10}%
							</Option>
						))}
					</Select>
				</FormControl>
				<FormControl sx={{ width: 200 }}>
					<InputLabel>Stroke style</InputLabel>
					<Select
						name="symbolStyle.stroke.style"
						onChange={handleChange}
						value={values.symbolStyle?.stroke?.style}>
						<Option
							key="none"
							value="none">
							None
						</Option>
						<Option
							key="solid"
							value="solid">
							Solid
						</Option>
						<Option
							key="dash"
							value="dash">
							Dash
						</Option>
						<Option
							key="dot"
							value="dot">
							Dot
						</Option>
					</Select>
				</FormControl>

				<FormControl sx={{ width: 200 }}>
					<InputLabel>Stroke width</InputLabel>
					<OutlinedInput
						sx={{ width: 200 }}
						name="symbolStyle.stroke.width"
						onChange={handleChange}
						onBlur={handleBlur}
						value={values.symbolStyle?.stroke?.width}
					/>
				</FormControl>
				<Button
					sx={{ width: 200 }}
					variant="outlined"
					onClick={() => onOpenCustomLegend()}>
					Customise legend
				</Button>
			</Stack>
			<CardActions sx={{ justifyContent: "flex-end", p: 0, mt: 2 }}>
				<Button color="secondary">Cancel</Button>
				<Button
					variant="contained"
					onClick={() => handleSubmit()}>
					Save changes
				</Button>
			</CardActions>
		</>
	);
};
